import type { CloudinaryImage } from "./types";

function resolveCloudName(): string {
  return (
    (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined)?.trim() ||
    "nm-creators"
  );
}

/**
 * Media kit delivery — quality first for brand evaluation.
 * Prefer limit (no forced crop) or fill+g_auto; never hard-crush quality.
 */
export type ImageOptions = {
  width?: number;
  height?: number;
  /** Default auto:good — sharper than plain q_auto for portfolio/media kits */
  quality?: "auto" | "auto:eco" | "auto:good" | "auto:best" | number;
  format?: "auto" | "webp" | "avif" | "jpg";
  /** limit = never upscale/crop edges; fill needs gravity */
  crop?: "fill" | "fit" | "limit" | "scale";
  gravity?: "auto" | "face" | "center";
  /** Keep true for retina; Cloudinary serves denser assets when needed */
  dprAuto?: boolean;
};

/** If publicId is a full Cloudinary delivery URL, extract path after /upload/ */
function parseCloudinaryDeliveryUrl(url: string): {
  cloudName: string;
  publicId: string;
  version?: string;
} | null {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("res.cloudinary.com")) return null;
    const parts = u.pathname.split("/").filter(Boolean);
    // /{cloud}/image/upload/[transforms/][v123/]public/id.ext
    const cloudName = parts[0];
    const uploadIdx = parts.indexOf("upload");
    if (!cloudName || uploadIdx === -1) return null;
    let rest = parts.slice(uploadIdx + 1);
    // Drop transform segment(s) if present (contain _ or ,)
    while (rest.length && (rest[0].includes(",") || /^[a-z]_[a-z0-9.,]+$/i.test(rest[0]))) {
      rest = rest.slice(1);
    }
    let version: string | undefined;
    if (rest[0] && /^v\d+$/.test(rest[0])) {
      version = rest[0];
      rest = rest.slice(1);
    }
    if (!rest.length) return null;
    const publicId = rest.join("/").replace(/\.[a-z0-9]+$/i, "");
    return { cloudName, publicId, version };
  } catch {
    return null;
  }
}

function buildTransformString(options: ImageOptions, image: CloudinaryImage): string {
  const quality = options.quality ?? "auto:good";
  const format = options.format ?? "auto";
  const crop = options.crop ?? "limit";
  const gravity =
    options.gravity ??
    (crop === "fill" ? "auto" : undefined);

  // NOTE: do NOT add fl_progressive.steep — returns HTTP 400 on many assets
  // in this cloud (breaks all media-kit images).
  const transforms = [
    `f_${format}`,
    // q_auto:good is valid; colon is fine in Cloudinary path segments
    `q_${quality}`,
    crop ? `c_${crop}` : undefined,
    gravity ? `g_${gravity}` : undefined,
    options.width ? `w_${options.width}` : undefined,
    options.height ? `h_${options.height}` : undefined,
    // Only one gravity — focalPoint wins when set
    !gravity && image.focalPoint
      ? `g_xy_center,x_${image.focalPoint.x},y_${image.focalPoint.y}`
      : undefined,
    options.dprAuto !== false ? "dpr_auto" : undefined,
  ]
    .filter(Boolean)
    .join(",");

  return transforms;
}

export function cloudinaryUrl(image: CloudinaryImage, options: ImageOptions = {}) {
  if (!image?.publicId) return "";

  const raw = image.publicId.trim();

  // Full Cloudinary URL → rebuild with quality-preserving transforms
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    const parsed = parseCloudinaryDeliveryUrl(raw);
    if (!parsed) {
      // Non-Cloudinary absolute URL: return as-is (cannot improve)
      return raw;
    }
    const transforms = buildTransformString(options, image);
    const version = image.version
      ? `/v${image.version}`
      : parsed.version
        ? `/${parsed.version}`
        : "";
    return `https://res.cloudinary.com/${parsed.cloudName}/image/upload/${transforms}${version}/${parsed.publicId}`;
  }

  const cloudName = resolveCloudName();
  if (!cloudName) return "";

  const transforms = buildTransformString(options, image);
  const version = image.version ? `/v${image.version}` : "";
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transforms}${version}/${raw}`;
}

/** Retina-friendly widths for media kit (portfolio quality on phone + desktop) */
const DEFAULT_SRCSET_WIDTHS = [480, 768, 1080, 1440, 1920];

export function cloudinarySrcSet(
  image: CloudinaryImage,
  widths = DEFAULT_SRCSET_WIDTHS,
  options: Omit<ImageOptions, "width"> = {},
) {
  const raw = image.publicId?.trim() ?? "";
  if (!raw) return undefined;
  // Can build srcset for public_ids and Cloudinary delivery URLs
  if (
    (raw.startsWith("http://") || raw.startsWith("https://")) &&
    !parseCloudinaryDeliveryUrl(raw)
  ) {
    return undefined;
  }

  return widths
    .map((width) => {
      const url = cloudinaryUrl(image, {
        quality: "auto:good",
        format: "auto",
        crop: "limit",
        dprAuto: false, // width descriptors already cover density
        ...options,
        width,
      });
      return `${url} ${width}w`;
    })
    .join(", ");
}
